import { Box, FormGroup } from '@mui/material';
import Button from '~/components/Button';
import { useCallback, useMemo, useRef, useState } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';
import { IUserDetails } from '~/data/lead/interfaces';
import RESOURCES from '~/resources';

import FormValidation from '~/Validators/FormValidation';

import {
  EOnKeyDownValidation,
  useValidateOnKeyDown,
} from '~/hooks/useValidateOnKeyDown';
import { RootState } from '~/redux-toolkit/store/store';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/redux-toolkit/store/hooks';
import {
  setCity,
  setFirstName,
  setLastName,
  setPhone,
} from '~/redux-toolkit/slices/contactUsSlice';
import CitiesAutocomplete from '~/components/CitiesAutocomplete';
import { CityOption } from '~/components/CitiesAutocomplete/citiesAutocompleteData';
import {
  useStyles,
  formGroupStyle,
  buttonStyle,
  helperTextStyle,
  customOptionsStyle,
  customLocationsInputStyle,
  userCarditionalStyle,
  customInnerStyle,
  hiddenErrorMessage,
} from './useStyles';
import { useIsUsedCar } from '~/hooks/useIsUsedCar';
import PhoneIconIcon from '~/assets/icons/jsx/PhoneIcon';
import { IDropdownDetails } from '~/components/MarketingPopup/components/ShortUserDetails/ShortUserDetails';
const CustomDropdownOrPopup = dynamic(
  () => import('~/components/CustomDropdownOrPopup'),
  {
    ssr: false,
  },
);
import SearchSelectInput from '~/components/SelectInput';
import LocationPinIcon from '~/assets/icons/jsx/LocationIcon';

import { useIsMobile } from '~/hooks/useIsMobile';
import { RentalLocationType } from '~/data/types/rentalLocationsType';

import { ARIA_LABELS_RESOURCES } from '~/resources/resources';
import CustomTextFieldV2 from '~/components/CustomTextFieldV2';
import dynamic from 'next/dynamic';
import CitiesInput from '../ContactUsPopup/components/CitiesInput';
import { useContactUsLeadBannerProps } from '~/hooks/useContactUsLeadBannerProps';

interface UserDetailsProps {
  // eslint-disable-next-line no-unused-vars
  onSubmit: (details: IUserDetails) => void;
  buttonLabel: string;
  loading: boolean;
  disabled?: boolean;
  isScheduleMeeting?: boolean;
  cities?: string[] | object;
  brands?: IDropdownDetails;
}

const ContactUsLead = () => {
  const {
    onSubmit,
    buttonLabel,
    loading,
    disabled,
    cities,
    isScheduleMeeting,
    brands,
  }: UserDetailsProps = useContactUsLeadBannerProps();

  const classes = useStyles;
  const dispatch = useAppDispatch();
  const cityRef = useRef<HTMLInputElement>(null);
  const { leadDetails, note, showNote } = useSelector(
    (state: RootState) => state.contactUsSlice,
  );

  const [selectedBrand] = useState<string>('');

  const { lastName, firstName, phone, email, city, mailsApproved } =
    leadDetails;
  const isUsedCar = useIsUsedCar();
  const isMobile = useIsMobile();

  function isStringArray(value: any): value is string[] {
    return Array.isArray(value);
  }

  const validateOnKeyDownLettersOnly = useValidateOnKeyDown(
    EOnKeyDownValidation.LETTERS,
  );
  const validateOnKeyDownDigitsOnly = useValidateOnKeyDown(
    EOnKeyDownValidation.DIGITS,
  );

  const handleFirstNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setFirstName(event.target.value));
    },
    [dispatch],
  );

  const handleLastNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setLastName(event.target.value));
    },
    [dispatch],
  );
  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setPhone(event.target.value));
    },
    [dispatch],
  );

  const handleCityChange = useCallback(
    (event: any, selectedCity: CityOption) => {
      if (isStringArray(cities) && cities?.includes(selectedCity.value)) {
        dispatch(setCity(selectedCity.value));
      } else {
        dispatch(setCity(''));
      }
      if (cityRef.current) {
        cityRef.current.blur();
      }
    },
    [cities, dispatch],
  );

  const onClicked = useCallback(() => {
    let details: IUserDetails = {
      firstName,
      lastName,
      phone,
      email,
      mailsApproved,
    };
    if (isUsedCar && !isScheduleMeeting) {
      details = { ...details, agency: city };
    } else {
      details = { ...details, city };
    }

    onSubmit(details);
  }, [
    firstName,
    lastName,
    phone,
    email,
    mailsApproved,
    isUsedCar,
    isScheduleMeeting,
    onSubmit,
    city,
  ]);

  const isBtnDisabled = useMemo(
    () =>
      !FormValidation.validateTextAndEmptySpaces(firstName) ||
      !FormValidation.validateTextAndEmptySpaces(lastName) ||
      !FormValidation.validateIsraeliCellPhone(phone) ||
      (email && !FormValidation.validateEmailEnglishOnly(email)) ||
      (cities && !city) ||
      (showNote && !note) ||
      (brands && !selectedBrand),
    [
      brands,
      cities,
      city,
      email,
      firstName,
      lastName,
      phone,
      selectedBrand,
      note,
      showNote,
    ],
  );

  const chooseCityTitle = useMemo(
    () =>
      isUsedCar && !isScheduleMeeting
        ? RESOURCES.CHOOSE_BRANCH
        : RESOURCES.CREATE_MEETING_SELECT_CITY_TITLE,
    [isScheduleMeeting, isUsedCar],
  );

  const chooseCityTitlePlaceholder = useMemo(
    () =>
      isUsedCar && !isScheduleMeeting
        ? RESOURCES.CHOOSE_BRANCH
        : RESOURCES.CREATE_MEETING_SELECT_CITY,
    [isScheduleMeeting, isUsedCar],
  );
  const [selectedCity, setSelectedCity] = useState('');
  const [isReturnDropdownOpen, setIsReturnDropdownOpen] = useState(false);

  const handleSelectLocation = useCallback(
    (location: RentalLocationType) => {
      setSelectedCity(location.name);
      setIsReturnDropdownOpen(false);
      dispatch(setCity(location?.tafnitName || ''));
    },
    [dispatch],
  );

  const handleToggleDropdown = useCallback(() => {
    setIsReturnDropdownOpen(!isReturnDropdownOpen);
  }, [isReturnDropdownOpen]);
  return (
    <div style={classes.root}>
      <FormGroup sx={formGroupStyle}>
        <Box sx={userCarditionalStyle}>
          <CustomTextFieldV2
            required
            disabled={disabled || loading}
            fullWidth
            label={RESOURCES.DETAILS_FIRST_NAME}
            placeholder={RESOURCES.DETAILS_FIRST_NAME}
            onChange={handleFirstNameChange}
            onKeyDown={validateOnKeyDownLettersOnly}
            error={!FormValidation.validateTextAndEmptySpaces(firstName)}
            helperText={RESOURCES.VALIDATION_HELPER_TEXT_INVALID_FIRST_NAME}
            value={firstName}
            customStyle={helperTextStyle}
            inputTypeName="firstName"
          />
          <CustomTextFieldV2
            required
            disabled={disabled || loading}
            fullWidth
            label={RESOURCES.DETAILS_LAST_NAME}
            placeholder={RESOURCES.DETAILS_LAST_NAME}
            onChange={handleLastNameChange}
            onKeyDown={validateOnKeyDownLettersOnly}
            error={!FormValidation.validateTextAndEmptySpaces(lastName)}
            helperText={RESOURCES.VALIDATION_HELPER_TEXT_INVALID_LAST_NAME}
            value={lastName}
            customStyle={helperTextStyle}
            inputTypeName="lastName"
          />
        </Box>

        <CustomTextFieldV2
          required
          disabled={disabled || loading}
          fullWidth
          label={phone.length > 0 ? RESOURCES.DETAILS_PHONE : ''}
          placeholder={RESOURCES.DETAILS_PHONE}
          icon={<PhoneIconIcon style={classes.iconStyle} />}
          onChange={handlePhoneChange}
          onKeyDown={validateOnKeyDownDigitsOnly}
          error={!FormValidation.validateIsraeliCellPhone(phone)}
          helperText={RESOURCES.VALIDATION_HELPER_TEXT_INVALID_PHONE_NUMBER}
          value={phone}
          customStyle={{ ...helperTextStyle, ...userCarditionalStyle }}
          inputTypeName="phone"
        />

        {cities && isStringArray(cities) && (
          <CitiesAutocomplete
            cities={cities}
            loading={loading}
            handleCityChange={handleCityChange}
            topLabel={chooseCityTitle}
            placeholder={chooseCityTitlePlaceholder}
            city={city}
            customStyle={helperTextStyle}
          />
        )}

        {cities && !isStringArray(cities) && (
          <CustomDropdownOrPopup
            inputSelected={
              <SearchSelectInput
                placeholder={chooseCityTitlePlaceholder}
                icon={<LocationPinIcon />}
                selected={selectedCity}
                isOpen={isReturnDropdownOpen}
                customStyle={customLocationsInputStyle}
                isArrow
              />
            }
            ariaLabel={`${chooseCityTitle} ${selectedCity} `}
            customOptionsStyle={customOptionsStyle}
            options={
              <CitiesInput
                title={RESOURCES.CHOOSE_BRANCH}
                branches={cities as Record<string, RentalLocationType[]>}
                onClickLocation={handleSelectLocation}
              />
            }
            handleCloseOptions={() => handleToggleDropdown()}
            isFullScreen={isMobile}
            isOpenOptions={isReturnDropdownOpen}
            setIsOpenOptions={setIsReturnDropdownOpen}
            customInnerStyle={customInnerStyle}
          />
        )}
      </FormGroup>
      <p
        aria-hidden="true"
        style={hiddenErrorMessage}
        id="leave-details-invalid-inputs"
      >
        {ARIA_LABELS_RESOURCES.DISABLED_FORM_BUTTON}
      </p>
      <Button
        label={buttonLabel}
        type={
          isBtnDisabled || loading
            ? BUTTON_TYPES.MAIN_DISABLED
            : BUTTON_TYPES.MAIN
        }
        size={BUTTON_SIZES.LARGE}
        onClickFunc={onClicked}
        customStyle={buttonStyle}
        loading={loading}
        isDisabled={isBtnDisabled}
        ariaDescribedBy={
          isBtnDisabled
            ? 'leave-details-invalid-inputs'
            : 'leave-details-approval-label'
        }
      />
    </div>
  );
};
export default ContactUsLead;
